import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { IAccount, IAccountDetail, ICertificateRequest, ICertificateDownloadRequest } from '@app/shared/models/banking.model';
import { IDynamicDialogConfig } from '@app/shared/modules/dynamic-dialog/model';
import { SubscriberComponent } from '@app/shared/modules/subscriber/base/subscriber.component.base';
import { AmplitudeService } from '@app/shared/services/amplitude.service';
import { AccountBasicService } from '@app/shared/services/banking/account-basic.service';
import { AccountContext } from '@app/shared/services/context/account.context.service';
import { CustomerContext } from '@app/shared/services/context/customer.context.service';
import { ModalService } from '@app/shared/services/modal.service';
import { ACTION_EVENT_PROPERTY, ACTION_NAME, AMPLITUDE_EVENT } from '@app/shared/types/enums/amplitude';
import { catchError, EMPTY, filter, switchMap, takeUntil, tap, of, from, concatMap, first, map, finalize, Observable, take, defaultIfEmpty, forkJoin } from 'rxjs';
import { Certificates } from '@app/shared/types/enums/certificates.enum';
import { STATUS } from '@app/shared/types/enums';
import { b64toBlob } from '@app/shared/utils/convert-b64-blob';
import FileSaver from 'file-saver';
import { trigger, state, transition, animate, style } from '@angular/animations';
import { UserService } from '@app/shared/services/onboarding/user.service';
import { TranslateService } from '@ngx-translate/core';
import { NotificationService } from '@app/shared/services/notification.service';
import { LoadingService } from '@app/shared/services/loading.service';

export enum CertificateCategory {
  SEND = 'send',
  DOWNLOAD = 'download',
}

export interface ICertificateOption {
  title: string;
  description: string;
  value: string;
  category: CertificateCategory;
  requiresYear: boolean;
  requiresActiveAccount?: boolean;
}

@Component({
  selector: 'app-certifications',
  templateUrl: './certifications.component.html',
  styleUrls: ['./certifications.component.sass'],
  animations: [trigger('fadeInOut', [state('void', style({ opacity: 0 })), transition('void <=> *', animate('0.5s ease-in-out'))])],
})
export class CertificationsComponent extends SubscriberComponent implements OnInit, OnDestroy {
  initialData!: IDynamicDialogConfig;
  certificateForm!: FormGroup;
  accountCode!: string;
  getAccounts: IAccount[] = [];
  account!: IAccount | IAccountDetail;
  years: number[] = [];
  yearsByCertificate: Map<string, number[]> = new Map();
  showYearField = false;
  isDownloadCategory = false;
  accountCreationYear: number | null = null;

  certificateOptions: ICertificateOption[] = [
    {
      title: 'MODAL_CERTIFICATIONS.ACCOUNT.PRODUCT_WITH_BALANCE.TITLE',
      description: 'MODAL_CERTIFICATIONS.ACCOUNT.PRODUCT_WITH_BALANCE.DESCRIPTION',
      value: Certificates.PRODUCT,
      category: CertificateCategory.SEND,
      requiresYear: false,
      requiresActiveAccount: true,
    },
    {
      title: 'MODAL_CERTIFICATIONS.ACCOUNT.PRODUCT_WITHOUT_BALANCE.TITLE',
      description: 'MODAL_CERTIFICATIONS.ACCOUNT.PRODUCT_WITHOUT_BALANCE.DESCRIPTION',
      value: Certificates.PRODUCT_WITHOUT_BALANCE,
      category: CertificateCategory.SEND,
      requiresYear: false,
      requiresActiveAccount: true,
    },
    {
      title: 'MODAL_CERTIFICATIONS.ACCOUNT.TAX_CERTIFICATE.TITLE',
      description: 'MODAL_CERTIFICATIONS.ACCOUNT.TAX_CERTIFICATE.DESCRIPTION',
      value: Certificates.TAX_CERTIFICATE,
      category: CertificateCategory.DOWNLOAD,
      requiresYear: true,
      requiresActiveAccount: false,
    },
    {
      title: 'MODAL_CERTIFICATIONS.ACCOUNT.RACT.TITLE',
      description: 'MODAL_CERTIFICATIONS.ACCOUNT.RACT.DESCRIPTION',
      value: Certificates.RACT,
      category: CertificateCategory.DOWNLOAD,
      requiresYear: true,
      requiresActiveAccount: false,
    },
  ];

  constructor(
    private readonly modalSvc: ModalService,
    private readonly _accountBasicsSvc: AccountBasicService,
    private readonly _accountCtx: AccountContext,
    private readonly _amplitudeSvc: AmplitudeService,
    private readonly _customerCtxSvc: CustomerContext,
    private readonly _userSvc: UserService,
    private readonly _translateSvc: TranslateService,
    private readonly _notificationSvc: NotificationService,
    private readonly _loadingSvc: LoadingService,
    private readonly cdr: ChangeDetectorRef
  ) {
    super();
  }

  ngOnInit() {
    this.getmodalData();
    this.initForm();
    this.logInitEvent();
    this.setupSubscriptions();

    this.accountCode = this._accountCtx.getCurrentAccount()?.code || '';
    this.getAccounts = this._accountCtx.getAccounts() || [];
    this.account = this.getAccounts.find((acc: any) => acc.code === this.accountCode) || ({} as IAccountDetail);
    this.setAccountCreationYear();
    this.loadAvailableYearsOnInit();
  }

  private setAccountCreationYear(): void {
    const accountDetail = this._accountCtx.getCurrentAccount();
    if (accountDetail?.created_at) {
      const createdDate = new Date(accountDetail.created_at);
      this.accountCreationYear = createdDate.getFullYear();
    }
  }

  private normalizeYears(years: number[]): number[] {
    const currentYear = new Date().getFullYear();

    return years.filter(year => year !== currentYear).sort((a, b) => b - a);
  }

  private loadAvailableYearsOnInit(): void {
    const user = this._customerCtxSvc.context;
    const account = this._accountCtx.getCurrentAccount();

    if (user?.rppUserId && account?.country) {
      this._loadingSvc.show();

      const ractYears$ = this._accountBasicsSvc.getCertificateYears(user.rppUserId, account.country, Certificates.RACT);
      const taxYears$ = this._accountBasicsSvc.getCertificateYears(user.rppUserId, account.country, Certificates.TAX_CERTIFICATE);

      this._subscriptions.sink = forkJoin({
        ractYears: ractYears$,
        taxYears: taxYears$
      })
        .pipe(
          tap(({ ractYears, taxYears }) => {
            this.yearsByCertificate.set(Certificates.RACT, this.normalizeYears(ractYears));
            this.yearsByCertificate.set(Certificates.TAX_CERTIFICATE, this.normalizeYears(taxYears));
            this.cdr.detectChanges();
          }),
          map(() => true),
          catchError(error => {
            console.error('Error loading certificate years:', error);
            this.yearsByCertificate.clear();
            this._notificationSvc.openSnackBar(
              this._translateSvc.instant('MODAL_CERTIFICATIONS.SNACK_BAR.ERROR'),
              3000,
              undefined,
              undefined,
              'error'
            );

            return of(true);
          }),
          finalize(() => this._loadingSvc.hide())
        )
        .subscribe();
    }
  }

  private loadYearsForCertificate(certificateType: string): void {
    const user = this._customerCtxSvc.context;
    const account = this._accountCtx.getCurrentAccount();

    if (user?.rppUserId && account?.country) {
      this._loadingSvc.show();

      this._subscriptions.sink = this._accountBasicsSvc
        .getCertificateYears(user.rppUserId, account.country, certificateType)
        .pipe(
          tap((years: number[]) => {
            const normalizedYears = this.normalizeYears(years);
            this.yearsByCertificate.set(certificateType, normalizedYears);
            this.years = normalizedYears;
            this.cdr.detectChanges();
          }),
          map(() => true),
          catchError(error => {
            console.error(`Error loading years for certificate ${certificateType}:`, error);
            this.yearsByCertificate.delete(certificateType);
            this.years = [];
            this._notificationSvc.openSnackBar(
              this._translateSvc.instant('MODAL_CERTIFICATIONS.SNACK_BAR.ERROR'),
              3000,
              undefined,
              undefined,
              'error'
            );

            return of(true);
          }),
          finalize(() => this._loadingSvc.hide())
        )
        .subscribe();
    }
  }

  isOptionDisabled(option: ICertificateOption): boolean {
    if (option.requiresActiveAccount && this.account?.status !== STATUS.ENABLE) {
      return true;
    }

    if (option.requiresYear) {
      const currentYear = new Date().getFullYear();
      const certificateYears = this.yearsByCertificate.get(option.value) || [];

      if (this.accountCreationYear === currentYear || certificateYears.length === 0) {
        return true;
      }
    }

    return false;
  }

  private logInitEvent(): void {
    this._amplitudeSvc.logEvent<ACTION_EVENT_PROPERTY>(AMPLITUDE_EVENT.ACTION_INIT, {
      actionName: ACTION_NAME.CERTIFICATES,
    });
  }

  private setupSubscriptions(): void {
    this.modalSvc.isPrimaryActionClicked$
      .pipe(
        takeUntil(this.destroy$),
        filter(value => value),
        switchMap(() => {
          const certificateType = this.certificateForm.get('certificateType')?.value;
          const selectedOption = this.certificateOptions.find(opt => opt.value === certificateType);

          // Set loading messages for download/send
          this.setLoadingMessages();

          if (selectedOption?.category === CertificateCategory.DOWNLOAD) {
            return this.handleDownloadCertificate().pipe(
              tap(() => {
                this.showSuccessMessage(true);
              }),
              finalize(() => this.modalSvc.loading(false))
            );
          } else {
            return this.handleSendCertificate().pipe(
              tap(() => {
                this.showSuccessMessage(false);
              }),
              finalize(() => this.modalSvc.loading(false))
            );
          }
        })
      )
      .subscribe();
  }

  private setLoadingMessages(): void {
    this.initialData = {
      ...this.initialData,
      modalComponentData: {
        ...this.initialData.modalComponentData,
        loaderTitle: 'MODAL_WITHDRAW.LOADER.TITLE',
        loaderSubtitle: 'MODAL_WITHDRAW.LOADER.SUBTITLE',
      },
    };
    this.modalSvc.setModalData(this.initialData);
  }

  private showSuccessMessage(isDownload: boolean): void {
    this.modalSvc.closeModal();

    const messageKey = isDownload
      ? 'MODAL_CERTIFICATIONS.SNACK_BAR.SUCCESS_CONTRACT'
      : 'MODAL_CERTIFICATIONS.SNACK_BAR.SUCCESS';

    this._notificationSvc.openSnackBar(
      this._translateSvc.instant(messageKey),
      3000,
      undefined,
      undefined,
      'green_check',
      ['snackbar-success']
    );
  }

  private handleSendCertificate() {
    const user = this._customerCtxSvc.context;
    const certificateType = this.certificateForm.get('certificateType')?.value;
    const showBalance = certificateType === Certificates.PRODUCT;

    const request: ICertificateRequest = {
      showBalance,
      toRole: 'Sr(a)',
      toName: user?.name || '',
      email: user?.email || '',
    };

    return this._accountBasicsSvc.sendCertificate(user?.rppUserId || '', this.accountCode, request).pipe(
      catchError(() => {
        this.modalSvc.closeWithErrorMsg(this.initialData.modalComponentData.modalName);

        return EMPTY;
      })
    );
  }

  private handleDownloadCertificate() {
    const user = this._customerCtxSvc.context;
    const certificateType = this.certificateForm.get('certificateType')?.value;
    const year = this.certificateForm.get('year')?.value;

    return this._userSvc.getKyc(user?.rppUserId || '').pipe(
      switchMap(kycData => {
        const identifications = kycData?.userData?.customer_data?.identifications || [];
        const userId = user?.userId || '';

        if (identifications.length === 0) {
          this.modalSvc.closeWithErrorMsg(this.initialData.modalComponentData.modalName);

          return EMPTY;
        }

        // Try each identification until one succeeds
        return from(identifications).pipe(
          concatMap((identification, index) => {
            const request: ICertificateDownloadRequest = {
              document_type: certificateType,
              document_date: year ? `${year}-01-01 00:00:00` : '',
              external_id: userId.toString(),
              document_id: identification?.number || '',
            };

            return this._accountBasicsSvc.downloadCertificate(user?.rppUserId || '', request).pipe(
              tap(res => {
                if (res) {
                  const file = b64toBlob(res.file || '', 'application/pdf');
                  FileSaver.saveAs(file, res.name);
                }
              }),
              catchError(error => {
                // Return of(null) to continue to next identification
                return of(null);
              })
            );
          }),
          // Take only the first successful result (non-null)
          filter((res: any) => res !== null),
          take(1),
          defaultIfEmpty(null),
          switchMap(result => {
            if (result === null) {
              this.modalSvc.closeWithErrorMsg(this.initialData.modalComponentData.modalName);

              return EMPTY;
            }

            return of(result);
          })
        );
      }),
      catchError(() => {
        this.modalSvc.closeWithErrorMsg(this.initialData.modalComponentData.modalName);

        return EMPTY;
      })
    );
  }

  private initForm(): void {
    this.certificateForm = new FormGroup({
      certificateType: new FormControl('', [Validators.required]),
      year: new FormControl(''),
    });
    this.onChangesForm();
  }

  private setInitialData(acceptButtonTitleDisabled: boolean, acceptButtonTitle?: string) {
    this.initialData = {
      ...this.initialData,
      acceptButtonTitle: acceptButtonTitle || this.initialData.acceptButtonTitle,
      modalComponentData: {
        ...this.initialData.modalComponentData,
        acceptButtonTitleDisabled,
      },
    };
    this.modalSvc.setModalData(this.initialData);
  }

  onChangesForm() {
    this._subscriptions.sink = this.certificateForm.get('certificateType')?.valueChanges.subscribe(value => {
      const selectedOption = this.certificateOptions.find(opt => opt.value === value);

      if (selectedOption) {
        this.showYearField = selectedOption.requiresYear;
        this.isDownloadCategory = selectedOption.category === CertificateCategory.DOWNLOAD;

        const yearControl = this.certificateForm.get('year');
        if (selectedOption.requiresYear) {
          yearControl?.setValidators([Validators.required]);
          // Load years on demand for the selected certificate type
          this.loadYearsForCertificate(selectedOption.value);
        } else {
          yearControl?.setValue('');
          yearControl?.clearValidators();
          this.years = [];
        }
        yearControl?.updateValueAndValidity({ emitEvent: false });

        const buttonText = this.isDownloadCategory ? 'MODAL_CERTIFICATIONS.DOWNLOAD_BUTTON' : 'MODAL_CERTIFICATIONS.SEND_BUTTON';
        this.setInitialData(!this.certificateForm.valid, buttonText);
      }
    });

    this._subscriptions.sink = this.certificateForm.get('year')?.valueChanges.subscribe(() => {
      this.certificateForm.updateValueAndValidity();
      const buttonText = this.isDownloadCategory ? 'MODAL_CERTIFICATIONS.DOWNLOAD_BUTTON' : 'MODAL_CERTIFICATIONS.SEND_BUTTON';
      this.setInitialData(!this.certificateForm.valid, buttonText);
    });
  }

  getmodalData() {
    this._subscriptions.sink = this.modalSvc.getModalData().subscribe({
      next: (data: IDynamicDialogConfig) => {
        this.initialData = data;
      },
    });
  }

  ngOnDestroy(): void {
    this.unsubscribe();
    this._subscriptions.unsubscribe();
  }
}
